<script lang="ts">
    import {Flex} from 'shared/layouts';
    import type {ViewableFile} from 'src/typings';
    import {getInjector} from 'src/core';
    import {AttendanceCalendar} from 'src/features/erp-sutor/features/attendance-calendar';
    import {LabeledValue} from 'src/features/erp-sutor/shared';
    import {SingularFileViewer} from 'src/features/media-viewer';
    import type {GridField, RecordRow} from 'src/features/record-grid/lib/types';
    import {createEventDispatcher, onMount} from 'svelte';
    import {getFirstFieldByFieldTypeIdRecursive} from 'src/features/record-grid/lib/grid-fields';
    import {exists} from 'shared/utils/custom-utils';
    import {getDateFromGridFieldValue, getTextFromGridFieldValue} from 'src/features/erp-sutor/sutor-utils';
    import {hasOriginRecordReference} from 'src/features/record-grid/lib/types-utils';
    import {getReportDetailContext} from '../report-detail.context';
    import {ReportDetailService} from '../report-detail.service';
    import {FOND_REPORT} from 'src/features/erp-sutor/sutor-fonds';
    import GridFieldValue from 'shared/ui-widgets/grid/GridFieldValue.svelte';
    import TextNoteEditor from './TextNoteEditor.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import ReportSameDayReportHoursSum from '../components/ReportSameDayReportHoursSum.svelte';

    export let record: RecordRow;
    export let attendanceWorkerField: GridField | null = null;

    const dispatch = createEventDispatcher<{ 'text-note-edit': string }>();
    const service = getInjector().getByClass(ReportDetailService);
    const context = getReportDetailContext();

    const referenceField = getFirstFieldByFieldTypeIdRecursive(record, FOND_REPORT.referenceFieldId);
    const dateField = getFirstFieldByFieldTypeIdRecursive(record, FOND_REPORT.dateFieldId);
    const topProjectField = getFirstFieldByFieldTypeIdRecursive(record, FOND_REPORT.topProjectFieldId);
    const closestProjectField = getFirstFieldByFieldTypeIdRecursive(record, FOND_REPORT.closestProjectFieldId);
    const projectField = getFirstFieldByFieldTypeIdRecursive(record, FOND_REPORT.projectFieldId);
    const textNoteField = getFirstFieldByFieldTypeIdRecursive(record, FOND_REPORT.textNoteFieldId);
    const responsiblePersonField = getFirstFieldByFieldTypeIdRecursive(record, FOND_REPORT.responsiblePersonFieldId);

    let textNoteModel = getTextFromGridFieldValue(textNoteField, '');
    $: dispatch('text-note-edit', textNoteModel);

    const approvedLuxonDate = getDateFromGridFieldValue(dateField);

    let referencePdfFile: ViewableFile | null = null;

    onMount(async () => {
        referencePdfFile = await service.getReportReferencePdfFile(referenceField);
    });
</script>

<Flex class="heading-container" direction="column" gap="ml">
    <slot name="heading"/>
</Flex>

<Flex class="details-container" wrap="wrap" columnGap="xxl" rowGap="ml">
    <LabeledValue label="Záznam výkazu">
        <a href="/#!/records/{record.id}">
            <IconedContent icon="eye">Zobrazit záznam</IconedContent>
        </a>
    </LabeledValue>

    <LabeledValue label="Zakázka/položka">
        <GridFieldValue lengthLimit={25} field={projectField} chipSize="sm"/>
    </LabeledValue>

    <LabeledValue label="Nejbližší zakázka">
        <GridFieldValue lengthLimit={25} field={closestProjectField} chipSize="sm"/>
    </LabeledValue>

    <LabeledValue label="Nosná zakázka">
        <GridFieldValue lengthLimit={25} field={topProjectField} chipSize="sm"/>
    </LabeledValue>

    <LabeledValue label="Datum">
        <GridFieldValue field={dateField} chipSize="sm"/>
    </LabeledValue>

    <LabeledValue label="Typ výkazu">
        <GridFieldValue value={FOND_REPORT.SHORTHAND_NAMES[record.fond.id]} chipSize="sm"/>
    </LabeledValue>

    <LabeledValue label="Odpovědný technik">
        <GridFieldValue field={responsiblePersonField} chipSize="sm"/>
    </LabeledValue>

    {#if exists($$slots.details)}
        <slot name="details"/>
    {/if}
</Flex>

<ReportSameDayReportHoursSum {record}/>

<TextNoteEditor bind:textNote={textNoteModel} editable={context.editableTextNote}/>

{#if exists(referencePdfFile)}
    <Flex direction="column" gap="s">
        <span class="heading-text">Průvodka</span>

        <div class="verbis-viewer-container">
            <SingularFileViewer file={referencePdfFile}/>
        </div>
    </Flex>
{/if}

{#if context.showAttendance}
    {#if exists(approvedLuxonDate) && exists(attendanceWorkerField) && hasOriginRecordReference(attendanceWorkerField)}
        <Flex direction="column" gap="s">
            <span class="heading-text">Docházka za poslední týden</span>
            <AttendanceCalendar highlightedDate={approvedLuxonDate}
                                userRecordId={attendanceWorkerField.originRecordReference.id} type="last-week"/>
        </Flex>
    {/if}
{/if}

<style lang="less">
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.themes.less";

    .heading-text {
        color: @themed-text-muted;
        font-size: @font-size-small;
    }

    .verbis-viewer-container {
        position: relative;
        height: 500px;
        border-top: 1px solid @themed-border-default;
        border-bottom: 1px solid @themed-border-default;
        margin-left: calc(@spacing-xl * -1);
        margin-right: calc(@spacing-xl * -1);
        width: calc(@spacing-xl * 2 + 100%);
    }
</style>
